import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RecentTx } from '../../../hooks/useDashboardData';

type FilterType = 'todas' | 'receita' | 'despesa';

interface Props {
  transactions: RecentTx[];
  loading: boolean;
}

/** Strip installment suffix "(N/M)" to get the base description */
const baseDescOf = (desc: string) => desc.replace(/\s*\(\d+\/\d+\)$/, '').trim();
const isInstallmentDesc = (desc: string) => /\(\d+\/\d+\)$/.test(desc.trim());

interface TxGroup {
  /** Unique key for this group */
  key: string;
  /** The representative transaction (earliest date or first encountered) */
  representative: RecentTx;
  /** Display description (base, without installment suffix) */
  displayDescription: string;
  /** Number of transactions in this group */
  count: number;
  /** Sum of amounts for grouped display */
  totalAmount: number;
  /** True when multiple installments of the same series were merged */
  isGrouped: boolean;
}

/**
 * Groups consecutive installment transactions of the same series.
 * Recurring transactions (no x/y notation) are never merged — each occurrence
 * is an independent event (different date, same value — that is intentional).
 */
const groupTransactions = (txs: RecentTx[]): TxGroup[] => {
  const map = new Map<string, TxGroup>();
  const order: string[] = [];

  txs.forEach((t) => {
    const base = baseDescOf(t.description);
    const isInstallment = isInstallmentDesc(t.description);

    // Only merge installment series; each recurring/standalone tx keeps its own key
    const key = isInstallment
      ? `${base}|${t.category}|${t.type}|${t.status}`
      : t.id;

    if (!map.has(key)) {
      map.set(key, {
        key,
        representative: t,
        displayDescription: base,
        count: 0,
        totalAmount: 0,
        isGrouped: false,
      });
      order.push(key);
    }

    const g = map.get(key)!;
    g.count += 1;
    g.totalAmount += t.amount;
    if (g.count > 1) g.isGrouped = true;
  });

  return order.map((k) => map.get(k)!);
};

const statusStyles: Record<string, string> = {
  pago: 'bg-emerald-100 text-emerald-700',
  recebido: 'bg-emerald-100 text-emerald-700',
  pendente: 'bg-amber-100 text-amber-700',
  vencido: 'bg-red-100 text-red-600',
  atrasado: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  pago: 'Pago',
  recebido: 'Recebido',
  pendente: 'Pendente',
  vencido: 'Vencido',
  atrasado: 'Atrasado',
};

const categoryIcons: Record<string, { icon: string; bg: string }> = {
  Renda: { icon: 'ri-briefcase-line', bg: 'bg-emerald-100' },
  Salário: { icon: 'ri-briefcase-line', bg: 'bg-emerald-100' },
  Freelance: { icon: 'ri-computer-line', bg: 'bg-violet-100' },
  Alimentação: { icon: 'ri-restaurant-line', bg: 'bg-orange-100' },
  Entretenimento: { icon: 'ri-film-line', bg: 'bg-red-100' },
  Moradia: { icon: 'ri-home-line', bg: 'bg-amber-100' },
  Saúde: { icon: 'ri-heart-pulse-line', bg: 'bg-pink-100' },
  Transporte: { icon: 'ri-car-line', bg: 'bg-sky-100' },
  Investimento: { icon: 'ri-line-chart-line', bg: 'bg-emerald-100' },
  Educação: { icon: 'ri-book-line', bg: 'bg-indigo-100' },
  Outros: { icon: 'ri-exchange-dollar-line', bg: 'bg-gray-100' },
};

const SkeletonRow = () => (
  <tr className="border-b border-gray-100 animate-pulse">
    <td className="px-6 py-3.5">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-gray-100 rounded-xl shrink-0" />
        <div className="h-3 bg-gray-100 rounded w-32" />
      </div>
    </td>
    <td className="px-4 py-3.5 hidden md:table-cell">
      <div className="h-3 bg-gray-100 rounded w-20" />
    </td>
    <td className="px-4 py-3.5 hidden lg:table-cell">
      <div className="h-3 bg-gray-100 rounded w-16" />
    </td>
    <td className="px-4 py-3.5">
      <div className="h-3 bg-gray-100 rounded w-14" />
    </td>
    <td className="px-4 py-3.5 hidden sm:table-cell">
      <div className="h-5 bg-gray-100 rounded-full w-16" />
    </td>
    <td className="px-6 py-3.5 text-right">
      <div className="h-3 bg-gray-100 rounded w-20 ml-auto" />
    </td>
  </tr>
);

const MAX_ROWS = 7;

export const RecentTransactions = ({ transactions, loading }: Props) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('todas');

  const filtered = transactions.filter(
    (t) => filter === 'todas' || t.type === filter
  );

  const groups = groupTransactions(filtered).slice(0, MAX_ROWS);

  const filterOptions: { label: string; value: FilterType }[] = [
    { label: 'Todas', value: 'todas' },
    { label: 'Receitas', value: 'receita' },
    { label: 'Despesas', value: 'despesa' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100">
      <div className="flex items-center justify-between p-6 pb-4">
        <div>
          <h3 className="text-base font-700 text-gray-900">Últimas Transações</h3>
          <p className="text-sm text-gray-500 mt-0.5">Movimentações recentes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {filterOptions.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 text-xs font-600 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  filter === f.value
                    ? 'bg-white text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/lancamentos')}
            className="px-3 py-2 text-xs font-600 text-emerald-600 hover:bg-emerald-50 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
          >
            Ver todas
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-y border-gray-100">
              <th className="px-6 py-3 text-left text-xs font-600 text-gray-500 uppercase tracking-wider">
                Descrição
              </th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 uppercase tracking-wider hidden md:table-cell">
                Categoria
              </th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Conta
              </th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 uppercase tracking-wider">
                Data
              </th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-600 text-gray-500 uppercase tracking-wider">
                Valor
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : groups.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                    <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl">
                      <i className="ri-exchange-dollar-line text-2xl text-gray-300" />
                    </div>
                    <div>
                      <p className="text-sm font-600 text-gray-500">
                        {filter === 'todas'
                          ? 'Nenhum lançamento ainda'
                          : filter === 'receita'
                          ? 'Nenhuma receita registrada'
                          : 'Nenhuma despesa registrada'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Seus lançamentos financeiros aparecerão aqui
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/lancamentos')}
                      className="text-xs text-emerald-600 font-600 hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Criar primeiro lançamento →
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              groups.map((g, i) => {
                const { representative: t, isGrouped, displayDescription, count, totalAmount } = g;
                const catInfo = categoryIcons[t.category] ?? categoryIcons['Outros'];
                return (
                  <tr
                    key={g.key}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                      i < groups.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-xl shrink-0 ${catInfo.bg}`}>
                          <i className={`${catInfo.icon} text-sm text-gray-600`} />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-600 text-gray-800 block truncate">
                            {displayDescription}
                          </span>
                          {isGrouped && (
                            <span className="text-[10px] font-600 text-gray-400">
                              {count} parcelas agrupadas
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg font-500">
                        {t.category}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <span className="text-xs text-gray-600 font-500">{t.account}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-gray-500">
                        {isGrouped
                          ? '—'
                          : t.date
                          ? new Date(t.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                            })
                          : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <span
                        className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${
                          statusStyles[t.status] ?? 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {statusLabels[t.status] ?? t.status}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <span
                        className={`text-sm font-700 ${
                          t.type === 'receita' ? 'text-emerald-600' : 'text-gray-900'
                        }`}
                      >
                        {t.type === 'receita' ? '+' : '-'}
                        {(isGrouped ? totalAmount : t.amount).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </span>
                      {isGrouped && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {(totalAmount / count).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                          /parcela
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
